import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Factory, Cog } from 'lucide-react';

export const ManufacturingPortfolio: React.FC = () => {
  const technologies = [
    'React', 'Java', 'MySQL', 'AJAX', 'REST APIs', 'AWS'
  ];

  const features = [
    'Product Catalog Management',
    'B2B Customer Portal',
    'Quote Request System',
    'Inventory Tracking',
    'Order Management',
    'Supplier Integration',
    'Quality Control Dashboard',
    'Reporting & Analytics'
  ];

  const results = [
    { metric: 'B2B Leads', value: '150%', description: 'Increase in qualified business leads' },
    { metric: 'Order Processing', value: '60%', description: 'Faster order processing time' },
    { metric: 'Customer Portal Usage', value: '88%', description: 'Of customers use online portal' },
    { metric: 'Operational Efficiency', value: '40%', description: 'Improvement in workflow efficiency' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Manufacturing
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Precision Components Inc.
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A comprehensive B2B manufacturing platform with advanced product catalog, customer portal, 
                and integrated order management that increased B2B leads by 150% and improved operational efficiency by 40%.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Manufacturing Facility"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  Precision Components Inc. needed a sophisticated B2B platform to showcase their 
                  extensive manufacturing capabilities, streamline customer interactions, and integrate 
                  with their existing ERP systems for seamless order processing.
                </p>
                <p className="mb-6">
                  We developed a comprehensive React and Java-based platform with advanced product 
                  catalog management, secure B2B customer portals, and integrated order management 
                  systems. The solution includes real-time inventory tracking, automated quote 
                  generation, and comprehensive reporting dashboards.
                </p>
                <p>
                  The implementation resulted in 150% increase in qualified B2B leads, 60% faster 
                  order processing, and 40% improvement in overall operational efficiency.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">18 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">July 2023</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Product Lines:</span>
                    <span className="font-medium">500+ components</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enterprise-grade manufacturing features designed to streamline B2B operations and enhance customer relationships.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Significant improvements in B2B customer engagement and operational efficiency.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Factory className="h-8 w-8 text-blue-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: ERP Integration</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Integrating the new platform with existing ERP systems while maintaining real-time 
                data synchronization and ensuring minimal disruption to operations.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed robust Java-based APIs with real-time data 
                synchronization, error handling, and fallback mechanisms to ensure seamless integration.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Cog className="h-8 w-8 text-orange-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Complex Product Catalog</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Managing a complex product catalog with technical specifications, custom configurations, 
                and dynamic pricing based on quantity and customer relationships.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Built a flexible catalog system with hierarchical product 
                organization, advanced filtering, and dynamic pricing engine with customer-specific rules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "OutstandingNet delivered a platform that has transformed our B2B operations. Our customers 
            love the self-service portal, and we've seen dramatic improvements in efficiency and lead generation."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="David Chen"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">David Chen</div>
              <div className="text-primary-200">Operations Director, Precision Components Inc.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Modernize Your Manufacturing Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create a B2B platform that streamlines operations and drives growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};